import React from "react";
import { TwoFaType as TwoFaTypeEnum } from "common/types/mainEnums";
import {
  type TwoFaActivationType,
  getActivationTypeValues,
} from "apps/common/authentication/common/utils";
import { useControl2FAMethodSelection } from "apps/portal/Portal/common/hooks";
import { type TwoFaType } from "common/types/mainTypes";
import { EmailSetupVariant } from "./Email2FASetup";

import SMS2FASetupVariant from "./SMS2FASetup";

import {
  type VerifyRedirectTotpState,
  type VerifyRedirectEmailState,
  type VerifyRedirectSMSState,
} from "./types";
import { type ActivationRedirectDestination } from "../hooks";
import { type UnauthenticatedArenaObjectType } from "../../types";
import TotpSetupVariant from "./Totp2FASetup";
import TwoFaSelection from "./TwoFaSelection";

export type onSubmitFnProps = {
  state?: VerifyRedirectSMSState | VerifyRedirectEmailState | VerifyRedirectTotpState;
  destination?: ActivationRedirectDestination;
};

type Props = {
  activationType?: TwoFaActivationType | undefined;
  onSubmit: ({ state, destination }: onSubmitFnProps) => void;
  arena?: UnauthenticatedArenaObjectType;
  isSplitScreen?: boolean;
  twoFaMethodOverride?: TwoFaType[];
  emailAddress?: string;
  password?: string;
};

/**
 * A container component to control the flow of the two-factor authentication setup process.
 * Enables the children component to either render the `SMS`, `Totp`, or `Email` setup components.
 */
const SetUpTwoFACard: React.FunctionComponent<Props> = ({
  onSubmit,
  activationType,
  arena,
  isSplitScreen,
  twoFaMethodOverride,
  emailAddress,
  password,
}) => {
  const {
    current2FAMethod,
    isShowing2FAMethodSelection,
    showEmail2FAMethod,
    showSMS2FAMethod,
    showTotp2FAMethod,
    show2FAMethodSelection,
    isLoading,
  } = useControl2FAMethodSelection(undefined, true, twoFaMethodOverride);

  const texts = getActivationTypeValues(activationType);
  const setShow2FAMethodSelectionTrue = () => {
    if (show2FAMethodSelection) {
      show2FAMethodSelection(true);
    }
  };

  if (isShowing2FAMethodSelection) {
    return (
      <TwoFaSelection
        onSwitch2FAMethodButtonClick={showEmail2FAMethod}
        arena={arena}
        isSplitScreen={isSplitScreen}
        onSubmit={() => {
          if (show2FAMethodSelection) {
            show2FAMethodSelection(false);
          }
        }}
        showTotp2FAMethod={showTotp2FAMethod}
        showSMS2FAMethod={showSMS2FAMethod}
        showEmail2FAMethod={showEmail2FAMethod}
        isLoading={isLoading}
      />
    );
  } else if (current2FAMethod === TwoFaTypeEnum.Sms) {
    return (
      <SMS2FASetupVariant
        arena={arena}
        onSwitch2FAMethodButtonClick={setShow2FAMethodSelectionTrue}
        onSubmit={(phoneNumber: string, countryCode: string) => {
          onSubmit({ state: { phoneNumber, countryCode } });
        }}
        title={texts.title}
        subtitle={texts.subtitle}
        submitButtonText={texts.buttonText}
        showHeader={texts.showHeader}
        isSplitScreen={isSplitScreen}
      />
    );
  } else if (current2FAMethod === TwoFaTypeEnum.Totp) {
    return (
      <TotpSetupVariant
        arena={arena}
        onSwitch2FAMethodButtonClick={setShow2FAMethodSelectionTrue}
        isSplitScreen={isSplitScreen}
        onSubmit={(totpCode?: string) => {
          onSubmit({ state: { isSettingUpTotp: true, totpCode: totpCode || "" } });
        }}
        emailAddress={emailAddress}
        password={password}
      />
    );
  }
  return (
    <EmailSetupVariant
      arena={arena}
      onSwitch2FAMethodButtonClick={setShow2FAMethodSelectionTrue}
      activationRedirectWithState={onSubmit}
      isSplitScreen={isSplitScreen}
    />
  );
};

export default SetUpTwoFACard;
